import React, { useState } from 'react';
        import { Helmet } from 'react-helmet';
        import { Mail, Phone, MapPin, MessageSquare, Send, CheckCircle } from 'lucide-react';
        import Header from '../../components/ui/Header';
        import Button from '../../components/ui/Button';

        const Contact = () => {
          const [formData, setFormData] = useState({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
          const [isSubmitting, setIsSubmitting] = useState(false);
          const [submitted, setSubmitted] = useState(false);

          const handleChange = (e) => {
            setFormData({
              ...formData,
              [e?.target?.name]: e?.target?.value
            });
          };

          const handleSubmit = async (e) => {
            e?.preventDefault();
            setIsSubmitting(true);
            
            // Simulate form submission
            setTimeout(() => {
              setIsSubmitting(false);
              setSubmitted(true);
              setFormData({ name: '', email: '', subject: '', message: '' });
              
              setTimeout(() => {
                setSubmitted(false);
              }, 5000);
            }, 1000);
          };

          return (
            <>
              <Helmet>
                <title>Contact Us - Selenium Sandbox</title>
                <meta name="description" content="Get in touch with Selenium Sandbox. Send us your questions, feedback, or suggestions." />
              </Helmet>
              <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
                <Header />

                {/* Hero Section */}
                <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                  <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                      Contact Us
                      <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        We're Here to Help
                      </span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                      Have questions about our platform? Need help with your learning journey? 
                      Or want to share feedback? We'd love to hear from you!
                    </p>
                  </div>
                </section>

                {/* Contact Form and Info */}
                <section className="pb-16 px-4 sm:px-6 lg:px-8">
                  <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12">
                      
                      {/* Contact Form */}
                      <div className="bg-white rounded-xl shadow-lg p-8">
                        <h2 className="text-2xl font-bold mb-6 flex items-center">
                          <MessageSquare className="w-6 h-6 mr-3 text-blue-600" />
                          Send us a Message
                        </h2>

                        {submitted ? (
                          <div className="text-center py-8">
                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-green-700 mb-2">
                              Message Sent Successfully!
                            </h3>
                            <p className="text-gray-600">
                              Thank you for contacting us. We'll get back to you within 24 hours.
                            </p>
                          </div>
                        ) : (
                          <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                  Full Name *
                                </label>
                                <input
                                  type="text"
                                  id="name"
                                  name="name"
                                  required
                                  value={formData?.name}
                                  onChange={handleChange}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  placeholder="Your full name"
                                />
                              </div>
                              <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                  Email Address *
                                </label>
                                <input
                                  type="email"
                                  id="email"
                                  name="email"
                                  required
                                  value={formData?.email}
                                  onChange={handleChange}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  placeholder="your@email.com"
                                />
                              </div>
                            </div>

                            <div>
                              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                Subject *
                              </label>
                              <select
                                id="subject"
                                name="subject"
                                required
                                value={formData?.subject}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              >
                                <option value="">Select a subject</option>
                                <option value="general">General Inquiry</option>
                                <option value="technical">Technical Support</option>
                                <option value="feedback">Feedback & Suggestions</option>
                                <option value="partnership">Partnership Opportunities</option>
                                <option value="bug">Bug Report</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <div>
                              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                Message *
                              </label>
                              <textarea
                                id="message"
                                name="message"
                                required
                                rows="6"
                                value={formData?.message}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                placeholder="Tell us more about your inquiry or feedback..."
                              />
                            </div>

                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full flex items-center justify-center space-x-2"
                              size="lg"
                            >
                              {isSubmitting ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  <span>Sending...</span>
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4" />
                                  <span>Send Message</span>
                                </>
                              )}
                            </Button>
                          </form>
                        )}
                      </div>

                      {/* Contact Information */}
                      <div className="space-y-8">
                        <div className="bg-white rounded-xl shadow-lg p-8">
                          <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
                          
                          <div className="space-y-6">
                            <div className="flex items-start space-x-4">
                              <Mail className="w-6 h-6 text-blue-600 mt-1" />
                              <div>
                                <h3 className="font-semibold text-gray-900">Email</h3>
                                <p className="text-gray-600">support@seleniumsandbox.com</p>
                                <p className="text-sm text-gray-500 mt-1">
                                  We typically respond within 24 hours
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-4">
                              <Phone className="w-6 h-6 text-blue-600 mt-1" />
                              <div>
                                <h3 className="font-semibold text-gray-900">Phone</h3>
                                <p className="text-gray-600">+1 (555) 123-4567</p>
                                <p className="text-sm text-gray-500 mt-1">
                                  Monday - Friday, 9 AM - 6 PM EST
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-4">
                              <MapPin className="w-6 h-6 text-blue-600 mt-1" />
                              <div>
                                <h3 className="font-semibold text-gray-900">Address</h3>
                                <p className="text-gray-600">
                                  123 Tech Street<br />
                                  San Francisco, CA 94102<br />
                                  United States
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* FAQ Section */}
                        <div className="bg-white rounded-xl shadow-lg p-8">
                          <h2 className="text-2xl font-bold mb-6">Quick Questions</h2>
                          
                          <div className="space-y-4">
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">
                                How do I get started with the platform?
                              </h3>
                              <p className="text-gray-600 text-sm">
                                Simply create a free account and explore our tutorial learning center to begin your automation testing journey.
                              </p>
                            </div>

                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">
                                Is the platform completely free?
                              </h3>
                              <p className="text-gray-600 text-sm">
                                Yes! Selenium Sandbox is 100% free forever with no credit card required.
                              </p>
                            </div>

                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">
                                Do you offer enterprise solutions?
                              </h3>
                              <p className="text-gray-600 text-sm">
                                Contact us about custom enterprise solutions and team training programs.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </>
          );
        };

        export default Contact;